/**
 * Lake Charles City Partnership Page
 *
 * Wrapper that imports the parameterized CityPartnershipPage from city-landing-core
 * and passes Lake Charles-specific configuration with full revenue calculations.
 *
 * This matches the legacy lake-charles-landing/pages/CityPartnershipPage.tsx structure
 * but uses the reusable @core component for consistency across cities.
 */

import React from 'react';
import { SEOHead } from '@/components/shared/seo/SEOHead';
import { CityPartnershipPage, CityConsultationForm } from '@core';
import { submitCityConsultation } from '../services/cityConsultationService';
import lakeCharlesConfig from '../city.config';
import { RevenueInfograph } from '../components/RevenueInfograph';

export function LakeCharlesCityPartnershipPage() {
  // Lake Charles specific data (matches legacy values exactly)
  const population = 80000;
  const residentCostSingleCity = '$0.60';
  const residentCostStatewide = '$0.35';
  const singleCityAnnualCost = '$576,000';
  const statewideAnnualCost = '$336,000';

  // Create wrapper component that matches expected props signature
  const LakeCharlesConsultationForm = ({
    open,
    onOpenChange,
  }: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }) => (
    <CityConsultationForm
      cityName={lakeCharlesConfig.identity.cityName}
      stateName={lakeCharlesConfig.identity.stateName}
      contactEmail="" /* "cities@toolboxlogic.com" */
      open={open}
      onOpenChange={onOpenChange}
      onSubmit={async (data) => {
        const res = await submitCityConsultation(data);
        return { success: res.success, error: res.error };
      }}
    />
  );

  // Revenue infograph with Lake Charles data
  const LakeCharlesRevenueInfograph = () => (
    <RevenueInfograph
      annualConsumerSpending="$6–8B"
      adoptionRate={0.3}
      cityRevenue="$3M–$5M+/yr"
      treasuryRevenue="$3M–$5M+/yr"
    />
  );

  return (
    <>
      <SEOHead
        title="City Partnership Program | Lake Charles Community App"
        description="Municipal revenue opportunity for Lake Charles: $3.6-4.8M/year from vendor transaction fees. Two models: passive revenue-sharing or optional city-sponsored universal access."
        canonical="https://lakecharles.toolboxlogic.com/city-partnership"
        keywords="city partnership, government technology, civic engagement, Lake Charles, municipal revenue"
        ogType="article"
        ogImage="https://lakecharles.toolboxlogic.com/media_lakecharles/og-lakecharles-1200x630.png"
      />
      <CityPartnershipPage
        cityName={lakeCharlesConfig.identity.cityName}
        stateName={lakeCharlesConfig.identity.stateName}
        population={population}
        annualConsumerSpending="$6B"
        annualIndustrialSpending="$19B"
        projectedCityRevenue="$3.6-4.8M/year"
        projectedTreasuryRevenue="$3.6-4.8M/year"
        residentCostSingleCity={residentCostSingleCity}
        residentCostStatewide={residentCostStatewide}
        singleCityAnnualCost={singleCityAnnualCost}
        statewideAnnualCost={statewideAnnualCost}
        processingRate="~2.9%"
        cityRevenueSharePercent="0.2%"
        treasurySharePercent="0.2%"
        potentialStatewidePartners={[
          'Lafayette',
          'Baton Rouge',
          'New Orleans',
          'Shreveport',
          'Alexandria',
        ]}
        RevenueInfographComponent={LakeCharlesRevenueInfograph}
        CityConsultationFormComponent={LakeCharlesConsultationForm}
      />
    </>
  );
}
